import React from "react"
import PropTypes from "prop-types"
import styled, { css } from "styled-components"
import { useField } from "formik"

import { ErrorMessage } from "./common"

import CheckmarkSVG from "../../images/icons/check--small.inline.svg"

const CheckboxContainer = styled.div`
  margin: 0 0 20px;
  padding: 0 0 0 30px;
  position: relative;

  ${(props) =>
    props.disabled &&
    css`
      ${CheckboxFaux} {
        cursor: default;
        opacity: 0.5;
      }
    `}
`

const CheckboxField = styled.input`
  border: 0;
  clip: rect(0, 0, 0, 0);
  height: 1px;
  left: 10px;
  margin: 0 10px 0 0;
  opacity: 0;
  overflow: hidden;
  padding: 0;
  position: absolute;
  white-space: nowrap;
  width: 1px;
`

export const CheckboxFaux = styled.div`
  align-items: center;
  border: 1px solid var(--color-sage);
  color: var(--color-spaceblue);
  cursor: pointer;
  display: flex;
  height: 21px;
  justify-content: center;
  left: 0;
  position: absolute;
  top: 0;
  width: 21px;

  ${CheckboxField}:focus + & {
    border-color: var(--color-sun);
    outline: 2px solid var(--color-sun);
    outline-offset: 3px;
  }

  ${(props) =>
    props.$error &&
    css`
      border-color: var(--color-error);
    `}

  svg {
    height: 18px;
    width: 18px;
  }
`

const CheckboxLabel = styled.label``

const Checkbox = ({
  children,
  className,
  disabled,
  hideRequired,
  forwardRef,
  onBlur,
  onFocus,
  required,
  ...props
}) => {
  const [field, meta] = useField({ ...props, type: "checkbox" })
  const fieldId = props.id || field.name
  const errorId = `${fieldId}-error`
  const hasError = meta.touched && meta.error

  return (
    <CheckboxContainer
      className={className}
      data-testid={`checkbox-${fieldId}`}
      disabled={disabled}
    >
      <CheckboxLabel htmlFor={fieldId}>
        <CheckboxField
          aria-describedby={hasError ? errorId : undefined}
          aria-invalid={hasError ? "true" : undefined}
          aria-required={required}
          disabled={disabled}
          id={fieldId}
          onBlur={onBlur}
          onFocus={onFocus}
          ref={forwardRef}
          type="checkbox"
          {...field}
          {...props}
        />
        <CheckboxFaux aria-hidden="true" $error={hasError} role="presentation">
          {field.checked && <CheckmarkSVG aria-hidden="true" />}
        </CheckboxFaux>
        {children}{" "}
        {required && !hideRequired && <span aria-hidden="true">*</span>}
      </CheckboxLabel>
      {hasError ? (
        <ErrorMessage id={errorId} role="alert">
          {meta.error}
        </ErrorMessage>
      ) : null}
    </CheckboxContainer>
  )
}

Checkbox.propTypes = {
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
  disabled: PropTypes.bool,
  forwardRef: PropTypes.object,
  hideRequired: PropTypes.bool,
  id: PropTypes.string,
  name: PropTypes.string.isRequired,
  onBlur: PropTypes.func,
  onFocus: PropTypes.func,
  required: PropTypes.bool,
}

export default Checkbox
